import {Color} from '@benchling/nucleus-ui';
import {Anchor, Box, Button, CloseButton, createStyles, Group, Stack, Text} from '@mantine/core';
import React from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export const FLOWCHART_FEEDBACK_URL = 'https://airtable.com/appamsfm4JmJBQrhT/pagKks3cFxpIitNBh/form';

interface FlowchartProcedureRunDesignOptOutBannerProps {
  onDismiss: () => void;
  onOptOut: () => void;
  procedureType: ProcedureType;
}

/**
 * Limited-availability banner shown in the flowchart designer that lets users
 * switch back to the legacy designer and share feedback.
 */
export default function FlowchartProcedureRunDesignOptOutBanner({
  onDismiss,
  onOptOut,
  procedureType,
}: FlowchartProcedureRunDesignOptOutBannerProps): JSX.Element {
  const {classes} = useStyles();
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });
  const procedureSentenceCaseString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.SENTENCE,
  });

  return (
    <FlowchartProcedureRunDesignBanner
      actionButton={
        <Button
          className={classes.actionButton}
          component="a"
          href={FLOWCHART_FEEDBACK_URL}
          leftIcon={<BenchlingIcon className={classes.actionButtonIcon} icon="message" />}
          rel="noopener noreferrer"
          target="_blank"
          variant="ghost"
          {...getTestProps({component: 'FlowchartProcedureRunDesignBanner', element: 'feedbackButton'})}
        >
          Share feedback
        </Button>
      }
      body={
        <>
          The new {procedureString} run designer is in limited availability.{' '}
          <Anchor
            className={classes.optOutLink}
            component="button"
            type="button"
            onClick={onOptOut}
            {...getTestProps({component: 'FlowchartProcedureRunDesignBanner', element: 'optOutLink'})}
          >
            Switch back to the legacy {procedureString} run designer
          </Anchor>{' '}
          anytime during this period.
        </>
      }
      onDismiss={onDismiss}
      title={`${procedureSentenceCaseString} run plans are now flowcharts`}
    />
  );
}

interface FlowchartProcedureRunDesignOptInBannerProps {
  onDismiss: () => void;
  onOptIn: () => void;
  procedureType: ProcedureType;
}

/**
 * Limited-availability banner shown in the legacy designer that invites users
 * to try the new flowchart designer.
 */
export function FlowchartProcedureRunDesignOptInBanner({
  onDismiss,
  onOptIn,
  procedureType,
}: FlowchartProcedureRunDesignOptInBannerProps): JSX.Element {
  const {classes} = useStyles();
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });
  const procedureSentenceCaseString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.SENTENCE,
  });

  return (
    <FlowchartProcedureRunDesignBanner
      actionButton={
        <Button
          className={classes.actionButton}
          leftIcon={<BenchlingIcon className={classes.actionButtonIcon} icon="sparkles" />}
          onClick={onOptIn}
          type="button"
          variant="ghost"
          {...getTestProps({
            component: 'FlowchartProcedureRunDesignBanner',
            element: 'tryNewDesignerButton',
          })}
        >
          Try the new designer
        </Button>
      }
      body={`The new ${procedureString} run designer is in limited availability. Try it and tell us what you think.`}
      onDismiss={onDismiss}
      title={`${procedureSentenceCaseString} runs have a new look`}
    />
  );
}

interface FlowchartProcedureRunDesignBannerProps {
  actionButton: React.ReactNode;
  body: React.ReactNode;
  onDismiss: () => void;
  title: string;
}

/**
 * Shared visual shell for the flowchart opt-in and opt-out banners.
 */
function FlowchartProcedureRunDesignBanner({
  actionButton,
  body,
  onDismiss,
  title,
}: FlowchartProcedureRunDesignBannerProps): JSX.Element {
  const {classes} = useStyles();

  return (
    <Group
      className={classes.root}
      noWrap={true}
      {...getTestProps({component: 'FlowchartProcedureRunDesignBanner', element: 'root'})}
    >
      <Stack className={classes.textStack}>
        <Text className={classes.title}>{title}</Text>
        <Text className={classes.body}>{body}</Text>
      </Stack>
      {actionButton}
      <Box>
        <CloseButton
          aria-label="Dismiss preview banner"
          className={classes.dismissButton}
          iconSize={16}
          onClick={onDismiss}
        />
      </Box>
    </Group>
  );
}

const useStyles = createStyles((theme) => ({
  root: {
    alignItems: 'flex-start',
    backgroundColor: theme.colors.yellow[1],
    borderLeft: `${theme.spacing.xs} solid ${theme.colors.yellow[8]}`,
    borderRadius: theme.radius.sm,
    width: '100%',
    gap: theme.spacing.md,
    padding: '9px 12px',
  },
  textStack: {
    flex: 1,
    gap: theme.spacing.xs,
    minWidth: 0,
  },
  title: {
    color: Color.GRAY_900,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.other.fontWeights.semibold,
    lineHeight: '16px',
  },
  body: {
    color: Color.GRAY_900,
    fontSize: theme.fontSizes.sm,
    lineHeight: '14px',
  },
  optOutLink: {
    color: Color.GRAY_900,
    fontSize: theme.fontSizes.sm,
    lineHeight: '14px',
    textDecoration: 'underline',
    textUnderlineOffset: '1px',
    '&:hover': {
      color: Color.GRAY_900,
      textDecoration: 'underline',
    },
  },
  dismissButton: {
    alignSelf: 'flex-start',
    color: Color.GRAY_500,
    height: theme.spacing.lg,
    marginTop: '1px',
    minHeight: theme.spacing.lg,
    minWidth: theme.spacing.lg,
    width: theme.spacing.lg,
  },
  actionButton: {
    alignSelf: 'center',
    border: `1px solid ${theme.colors.gray[4]}`,
    borderRadius: theme.radius.sm,
    color: Color.GRAY_900,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.other.fontWeights.semibold,
    lineHeight: '14px',
    '&:hover': {
      color: 'unset',
      textDecoration: 'unset',
    },
  },
  actionButtonIcon: {
    marginRight: theme.spacing.xs,
  },
}));
